<template>
	<app-modal :is-open="isOpen" :title="title" @close="close">
		<div class="space-y-4">
			<!-- Contract Info -->
			<div class="p-3 bg-blue-50 rounded-lg border border-blue-200">
				<div class="flex items-center justify-between">
					<div class="text-sm">
						<span class="font-medium">Contract Depot: </span>
						<span class="font-semibold text-blue-700">{{ contractDepot }}</span>
					</div>
					<div v-if="contract.delivery_date" class="text-sm">
						<span class="font-medium">Contract Delivery Date: </span>
						<span class="font-semibold text-blue-700">{{
							formatDate(contract.delivery_date)
						}}</span>
					</div>
				</div>
			</div>

			<!-- Info Note: Excluded Items -->
			<div
				v-if="excludedSaleGroupItems.length > 0"
				class="p-3 bg-amber-50 rounded-lg border border-amber-200">
				<div class="text-xs text-amber-700 font-medium mb-1.5">
					<font-awesome-icon icon="fas fa-exclamation-circle" class="mr-1" />
					The following Sale Group items are excluded from stock shortage requests:
				</div>
				<ul class="space-y-0.5 pl-4">
					<li
						v-for="item in excludedSaleGroupItems"
						:key="item.id"
						class="text-xs text-amber-700 list-disc">
						<span class="font-medium">{{ item.item_number }}</span>
						<span class="mx-1 text-amber-500">—</span>
						{{ item.description }}
						<span class="text-amber-500 ml-1">(qty: {{ item.quantity_required }})</span>
					</li>
				</ul>
			</div>

			<!-- Bulk Date Setter -->
			<div
				v-if="shortageItems.length > 0 || extraStockItems.length > 0"
				class="p-3 bg-blue-50 rounded-lg border border-blue-200">
				<div class="flex items-center gap-3">
					<app-label class="font-medium text-sm whitespace-nowrap"
						>Required By:</app-label
					>
					<div class="flex-1 max-w-xs">
						<app-date-input
							v-model="bulkRequiredByDate"
							:min-date="minDate"
							size="small"
							placeholder="Select date for all items"
							class="w-full" />
					</div>
					<app-button
						variant="secondary"
						size="small"
						:disabled="!bulkRequiredByDate"
						@click="applyBulkDate">
						<font-awesome-icon icon="fas fa-check" class="mr-1" />
						Apply to All
					</app-button>
				</div>
				<div class="text-xs text-blue-700 mt-2">
					<font-awesome-icon icon="fas fa-info-circle" class="mr-1" />
					You can override individual item dates after applying
				</div>
			</div>

			<!-- Shortage Items Grid -->
			<div v-if="shortageItems.length > 0" class="border rounded-lg bg-white">
				<div class="p-3 bg-gray-50 border-b">
					<app-label class="font-medium text-base"
						>Items with Shortages ({{ shortageItems.length }})</app-label
					>
				</div>

				<!-- Grid Header -->
				<div class="grid grid-cols-9 gap-2 p-3 bg-gray-100 border-b text-xs font-semibold">
					<div class="col-span-4">Item Code</div>
					<div class="col-span-1 text-center">Required</div>
					<div class="col-span-1 text-center">Available</div>
					<div class="col-span-1 text-center">Shortage</div>
					<div class="col-span-1 text-center">Request Qty</div>
					<div class="col-span-1 text-center">Required By</div>
				</div>

				<!-- Grid Body -->
				<div class="max-h-96 overflow-y-auto">
					<div
						v-for="item in shortageItems"
						:key="item.item_code"
						class="grid grid-cols-9 gap-2 p-3 border-b hover:bg-gray-50 items-center text-sm">
						<!-- Item Code -->
						<div
							class="col-span-4 font-medium truncate"
							:title="item.item_code + ' ' + item.description">
							{{ item.item_code }}
							<div class="text-xs text-gray-500 truncate">{{ item.description }}</div>
						</div>
						<!-- Required -->
						<div class="col-span-1 text-center">{{ item.quantity_required }}</div>
						<!-- Available -->
						<div class="col-span-1 text-center">{{ item.quantity_available }}</div>
						<!-- Shortage -->
						<div class="col-span-1 text-center text-red-600 font-semibold">
							{{ item.shortage }}
						</div>
						<!-- Request Qty -->
						<div class="col-span-1 flex justify-center">
							<app-input
								v-model.number="item.quantity_requested"
								type="number"
								:min="0"
								:max="item.shortage"
								size="small"
								class="w-full" />
						</div>
						<!-- Required By -->
						<div class="col-span-1 flex justify-center">
							<app-date-input
								v-model="item.required_by_date"
								:min-date="minDate"
								size="small"
								class="w-full" />
						</div>
					</div>
				</div>
			</div>

			<!-- No Shortages Message -->
			<app-alert v-else type="success" class="mx-4">
				No stock shortages found for {{ contractDepot }}. All items have sufficient stock.
			</app-alert>

			<!-- Extra Stock Section (Expandable) -->
			<div v-if="extraStockItems.length > 0" class="border rounded-lg bg-white">
				<button
					class="w-full p-3 bg-gray-50 border-b text-left hover:bg-gray-100 transition-colors flex items-center justify-between"
					@click="showExtraStock = !showExtraStock">
					<div class="flex items-center space-x-2">
						<app-label class="font-medium text-base"
							>Request Extra Stock ({{ extraStockItems.length }} items
							available)</app-label
						>
					</div>
					<svg
						:class="{ 'rotate-180': showExtraStock }"
						class="w-5 h-5 transition-transform"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M19 9l-7 7-7-7" />
					</svg>
				</button>

				<div v-if="showExtraStock">
					<!-- Grid Header -->
					<div
						class="grid grid-cols-9 gap-2 p-3 bg-gray-100 border-b text-xs font-semibold">
						<div class="col-span-4">Item Code</div>
						<div class="col-span-1 text-center">Required</div>
						<div class="col-span-1 text-center">Available</div>
						<div class="col-span-1 text-center">Shortage</div>
						<div class="col-span-1 text-center">Request Qty</div>
						<div class="col-span-1 text-center">Required By</div>
					</div>

					<!-- Grid Body -->
					<div class="max-h-96 overflow-y-auto">
						<div
							v-for="item in extraStockItems"
							:key="item.item_code"
							class="grid grid-cols-9 gap-2 p-3 border-b hover:bg-gray-50 items-center text-sm">
							<!-- Item Code -->
							<div
								class="col-span-4 font-medium truncate"
								:title="item.item_code + ' ' + item.description">
								{{ item.item_code }}
								<div class="text-xs text-gray-500 truncate">
									{{ item.description }}
								</div>
							</div>
							<!-- Required -->
							<div class="col-span-1 text-center">{{ item.quantity_required }}</div>
							<!-- Available -->
							<div class="col-span-1 text-center text-green-600 font-semibold">
								{{ item.quantity_available }}
							</div>
							<!-- Shortage -->
							<div class="col-span-1 text-center text-gray-400">0</div>
							<!-- Request Qty -->
							<div class="col-span-1 flex justify-center">
								<app-input
									v-model.number="item.quantity_requested"
									type="number"
									:min="0"
									size="small"
									class="w-full" />
							</div>
							<!-- Required By -->
							<div class="col-span-1 flex justify-center">
								<app-date-input
									v-model="item.required_by_date"
									:min-date="minDate"
									size="small"
									class="w-full" />
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Request Details -->
			<div v-if="shortageItems.length > 0" class="p-4 bg-gray-50 rounded-lg">
				<app-label class="block mb-2 font-medium">Request Details</app-label>
				<app-input
					v-model="request.request_details"
					type="textarea"
					placeholder="Please provide any additional details about this stock shortage request..."
					:rows="3" />
			</div>
		</div>

		<template #footer>
			<!-- Validation Warning -->
			<app-alert
				v-if="!canSubmit && (shortageItems.length > 0 || extraStockItems.length > 0)"
				theme="warning"
				class="mb-4">
				{{ getSubmitDisabledReason }}
			</app-alert>

			<div class="flex justify-end space-x-2">
				<app-button variant="secondary" @click="close">Cancel</app-button>
				<app-button
					:disabled="!canSubmit"
					:loading="submitting"
					variant="primary"
					@click="submitRequest">
					Submit Request
				</app-button>
			</div>
		</template>
	</app-modal>
</template>

<script lang="ts" setup>
	import { ref, computed, watch } from 'vue'
	import type { Contract, ContractItem } from '@/data/schema/contract'
	import type { Depot } from '@/data/schema/depot'
	import type { StockShortageRequest, StockShortageItem } from '@/data/schema/stock-shortage'
	import StockService from '@/services/stock'
	import useToaster from '@/stores/toaster'
	import { getPickingList } from '@/utils/contract-items'
	import AppModal from '@/components/base/AppModal.vue'
	import AppLabel from '@/components/base/forms/AppLabel.vue'
	import AppInput from '@/components/base/forms/AppInput.vue'
	import AppButton from '@/components/base/AppButton.vue'
	import AppDateInput from '@/components/base/forms/AppDateInput.vue'
	import AppAlert from '@/components/base/AppAlert.vue'
	import useAuthStore from '@/stores/auth'

	interface ShortageItem extends ContractItem {
		quantity_available: number
		shortage: number
		quantity_requested: number
		required_by_date: string
		notes?: string
	}

	const props = defineProps<{
		contract: Contract
		items: ContractItem[]
		depots: Depot[]
		stock: any[]
	}>()

	const emit = defineEmits<{
		close: []
		submitted: [request: StockShortageRequest]
	}>()

	const isOpen = ref(true)
	const contractDepot = ref<string>(props.contract.hire_depot)
	const shortageItems = ref<ShortageItem[]>([])
	const extraStockItems = ref<ShortageItem[]>([])
	const showExtraStock = ref(false)
	const submitting = ref(false)
	const bulkRequiredByDate = ref<string>('')

	console.log('StockShortageForm mounted, isOpen:', isOpen.value)
	console.log('props.contract:', props.contract)
	console.log('contractDepot (hire_depot):', contractDepot.value)
	console.log('props.items length:', props.items.length)
	console.log('props.depots:', props.depots)
	console.log('props.stock length:', props.stock.length)

	const authStore = useAuthStore()

	const request = ref<StockShortageRequest>({
		contract_number: props.contract.contract_number,
		depot: contractDepot.value,
		requested_by: authStore.user?.name ?? '',
		requested_at: new Date().toISOString(),
		request_details: '',
		items: []
	})

	const title = computed(() => `Request Stock Shortage - ${props.contract.contract_number}`)

	const excludedSaleGroupItems = computed(() =>
		props.items.filter((item) => item.type === 'Sale Group')
	)

	const minDate = computed(() => new Date().toISOString().split('T')[0])

	const formatDate = (dateString: string): string => {
		if (!dateString) return ''
		const date = new Date(dateString)
		return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
	}

	const getAvailableStock = (code: string, depot: string) => {
		// Match the StockList logic - only use quantity_available by default
		// This matches the default stockStatuses in StockList
		const stockItem = props.stock.find((item) => item.depot === depot && item.grp_code === code)
		if (stockItem) {
			// Only count quantity_available (matching StockList default behavior)
			return stockItem.quantity_available || 0
		}
		return 0
	}

	const calculateShortages = () => {
		console.log('calculateShortages called for depot:', contractDepot.value)
		console.log('props.items:', props.items)
		console.log('props.stock:', props.stock)

		// Use getPickingList to process items the same way StockList does
		// This flattens kit components, merges quantities, and sorts by item_code
		const pickingList = getPickingList(props.items, false, ['Unallocated', 'Quote'])
		console.log('pickingList after processing:', pickingList)

		// Filter out Sale Group items for the shortage request form
		const filteredPickingList = pickingList.filter((item) => {
			return item.type !== 'Sale Group'
		})

		const shortages: ShortageItem[] = []
		const extraStock: ShortageItem[] = []

		filteredPickingList.forEach((item) => {
			const available = getAvailableStock(item.item_code, contractDepot.value)
			const shortage = item.quantity_required - available

			// Match StockList logic:
			// - Green: quantity <= stock (sufficient)
			// - Yellow: quantity > stock && stock !== 0 (partial shortage)
			// - Red: !stock || stock <= 0 (complete shortage)
			// Both yellow and red are shortages
			const isShortage = item.quantity_required > available

			console.log(
				`Item ${item.item_code}: required=${item.quantity_required}, available=${available}, shortage=${shortage}, isShortage=${isShortage}`
			)

			if (isShortage) {
				// This is a shortage (red or yellow in the grid)
				shortages.push({
					...item,
					quantity_available: available,
					shortage,
					quantity_requested: shortage,
					required_by_date: '',
					notes: ''
				})
			} else {
				// Items with sufficient stock (green in the grid) - available for extra stock requests
				// Do not default depot or quantity for extra stock
				extraStock.push({
					...item,
					quantity_available: available,
					shortage: 0,
					quantity_requested: 0,
					required_by_date: '',
					notes: ''
				})
			}
		})

		// No need to sort - getPickingList already sorts by item_code

		console.log('final shortages:', shortages)
		console.log('final extraStock:', extraStock)
		console.log('extraStock.length:', extraStock.length)
		shortageItems.value = shortages
		extraStockItems.value = extraStock
		console.log('extraStockItems.value.length:', extraStockItems.value.length)
	}

	// Initialize shortages on mount
	calculateShortages()

	const applyBulkDate = () => {
		if (!bulkRequiredByDate.value) return

		// Apply to all shortage items
		shortageItems.value.forEach((item) => {
			item.required_by_date = bulkRequiredByDate.value
		})

		// Apply to all extra stock items
		extraStockItems.value.forEach((item) => {
			item.required_by_date = bulkRequiredByDate.value
		})

		useToaster().success({
			text: `Applied date to ${shortageItems.value.length + extraStockItems.value.length} items`
		})
	}

	const getSubmitDisabledReason = computed(() => {
		const requestedShortageItems = shortageItems.value.filter(
			(item) => item.quantity_requested > 0
		)
		const requestedExtraItems = extraStockItems.value.filter(
			(item) => item.quantity_requested > 0
		)
		const hasRequests = requestedShortageItems.length > 0 || requestedExtraItems.length > 0

		if (!hasRequests) {
			return 'Please request at least one item to submit the request.'
		}

		// Check if any requested items are missing dates
		const itemsWithoutDates = [
			...requestedShortageItems.filter((item) => !item.required_by_date),
			...requestedExtraItems.filter((item) => !item.required_by_date)
		]

		if (itemsWithoutDates.length > 0) {
			return `${itemsWithoutDates.length} requested item${itemsWithoutDates.length !== 1 ? 's' : ''} missing "Required By" date. Use the bulk date setter or set dates individually.`
		}

		return ''
	})

	const canSubmit = computed(() => {
		const requestedShortageItems = shortageItems.value.filter(
			(item) => item.quantity_requested > 0
		)
		const requestedExtraItems = extraStockItems.value.filter(
			(item) => item.quantity_requested > 0
		)
		const hasRequests = requestedShortageItems.length > 0 || requestedExtraItems.length > 0

		if (!hasRequests) return false

		// Check if all requested items have dates
		const allHaveDates = [...requestedShortageItems, ...requestedExtraItems].every(
			(item) => item.required_by_date
		)

		return allHaveDates
	})

	const close = () => {
		isOpen.value = false
		emit('close')
	}

	const buildRequestItem = (
		item: ShortageItem,
		type: 'shortage' | 'extra'
	): StockShortageItem => ({
		item_code: item.item_code,
		description: item.description,
		quantity_required: item.quantity_required,
		quantity_available: item.quantity_available,
		quantity_requested: item.quantity_requested,
		required_by_date: item.required_by_date,
		type,
		shortage_amount: type === 'shortage' ? item.shortage : undefined,
		item_weight: item.weight ? parseFloat(item.weight) : undefined
	})

	const submitRequest = async () => {
		if (!canSubmit.value) return

		submitting.value = true

		try {
			const requestItems = [
				...shortageItems.value
					.filter((item) => item.quantity_requested > 0)
					.map((item) => buildRequestItem(item, 'shortage')),
				...extraStockItems.value
					.filter((item) => item.quantity_requested > 0)
					.map((item) => buildRequestItem(item, 'extra'))
			]

			const requestData: StockShortageRequest = {
				contract_number: request.value.contract_number,
				depot: request.value.depot,
				requested_by: authStore.user?.name ?? authStore.user?.email ?? 'Unknown User',
				requested_at: new Date().toISOString(),
				request_details: request.value.request_details,
				items: requestItems
			}

			const stockService = new StockService()
			await stockService.createShortageRequest(requestData)

			emit('submitted', requestData)
			close()

			useToaster().success({ text: 'Stock shortage request submitted successfully' })
		} catch (error) {
			console.error('Error submitting shortage request:', error)
			useToaster().error({ text: 'Failed to submit stock shortage request' })
		} finally {
			submitting.value = false
		}
	}

	// Watch for modal close
	watch(isOpen, (newVal) => {
		if (!newVal) {
			emit('close')
		}
	})
</script>
